import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Car,
  FileJson,
  ListChecks,
  MapPin,
  RotateCw,
  UserRound,
} from "lucide-react";
import type { PolicyInfo } from "@ir/types";
import {
  formatDate,
  formatDateTime,
  formatLabel,
  formatYesNo,
  fullName,
  getLeadById,
  serializeDaveLeadPayload,
} from "@ir/services";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CopyButton,
  CopyableId,
  DetailList,
  ErrorState,
  LoadingState,
  PageHeader,
  type DetailItem,
} from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { useAsyncData } from "../hooks/useAsyncData";

/** Fields already shown in a dedicated section, so "everything else" can skip them. */
const KNOWN_INFO_KEYS = new Set([
  "id",
  "isMarketPlaceFormChecker",
  "vehicleReg",
  "vehicleMake",
  "vehicleModel",
  "year",
  "engineSize",
  "currentValue",
  "numberOfSeats",
  "title",
  "firstName",
  "surname",
  "dateOfBirth",
  "email",
  "mobile",
  "otherPhone",
  "postcode",
  "addressLine1",
  "addressLine2",
  "townOrCity",
  "county",
  "typeOfDrivingLicence",
  "testPassDate",
  "useOfVehicle",
  "annualMileage",
]);

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return formatYesNo(value);
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "object" ? JSON.stringify(entry) : String(entry)))
      .join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function LeadDetailsPage() {
  const { can } = useAuth();
  const { id = "" } = useParams();
  const result = useAsyncData((signal) => getLeadById(id, signal), [id]);
  const lead = result.data;

  if (result.loading) return <LoadingState message="Loading lead…" />;

  if (result.error || !lead) {
    return (
      <>
        <PageHeader
          icon={ListChecks}
          title="Lead"
          breadcrumbs={[{ label: "Leads", href: "/leads" }, { label: "Not found" }]}
          linkComponent={({ href, children }) => <Link to={href}>{children}</Link>}
        />
        <Card padded={false}>
          <ErrorState
            title="We could not open this lead"
            message={result.error ?? "That lead no longer exists."}
            onRetry={result.reload}
          />
        </Card>
      </>
    );
  }

  const info = (lead.info ?? {}) as PolicyInfo;

  // The step form keeps adding fields, so anything not explicitly placed above
  // is still shown rather than silently dropped.
  const extraItems: DetailItem[] = Object.entries(info)
    .filter(
      ([key, value]) =>
        !KNOWN_INFO_KEYS.has(key) &&
        !Array.isArray(value) &&
        typeof value !== "object" &&
        value !== null &&
        value !== undefined &&
        value !== "",
    )
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ label: formatLabel(key), value: renderValue(value) }));

  const arraySections = (
    [
      ["driverOccupations", "Occupations"],
      ["motorClaims", "Motor claims"],
      ["motoringConvictionEntries", "Motoring convictions"],
      ["nonMotoringConvictions", "Non-motoring convictions"],
      ["additionalDrivers", "Additional drivers"],
    ] as const
  )
    .map(([key, label]) => ({
      label,
      rows: (info[key] as Record<string, unknown>[] | undefined) ?? [],
    }))
    .filter((section) => section.rows.length > 0);

  return (
    <>
      <PageHeader
        icon={ListChecks}
        title={fullName([lead.first_name, lead.surname], "Lead details")}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {lead.type && <Badge tone="gold">{lead.type}</Badge>}
            {lead.partial ? (
              <Badge tone="warning" dot>
                Partial submission
              </Badge>
            ) : (
              <Badge tone="success" dot>
                Complete
              </Badge>
            )}
            <span className="text-xs">
              Created {formatDateTime(lead.created_at ?? lead.timestamp ?? lead._ts)}
            </span>
          </span>
        }
        breadcrumbs={[{ label: "Leads", href: "/leads" }, { label: "Details" }]}
        linkComponent={({ href, children }) => <Link to={href}>{children}</Link>}
        actions={
          <>
            <Link to="/leads">
              <Button variant="ghost" icon={ArrowLeft}>
                Back
              </Button>
            </Link>
            <Button
              variant="secondary"
              icon={RotateCw}
              loading={result.refreshing}
              onClick={result.reload}
            >
              Refresh
            </Button>
            {can("devtools.use") && (
              <CopyButton value={serializeDaveLeadPayload(lead)} label="Copy Marketplace JSON" />
            )}
          </>
        }
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader icon={UserRound} title="Contact" />
          <DetailList
            columns={1}
            items={[
              { label: "Lead ID", value: <CopyableId value={lead.id} truncate={false} /> },
              {
                label: "Customer reference",
                value: lead.reference ? (
                  <CopyableId value={lead.reference} truncate={false} />
                ) : null,
                hideWhenEmpty: true,
              },
              {
                label: "Created",
                value: formatDateTime(lead.created_at ?? lead.timestamp ?? lead._ts),
              },
              { label: "Name", value: fullName([info.title, lead.first_name, lead.surname]) },
              { label: "Email", value: lead.email },
              { label: "Mobile", value: lead.mobile },
              { label: "Other phone", value: lead.other_phone, hideWhenEmpty: true },
              { label: "Date of birth", value: formatDate(info.dateOfBirth) },
              { label: "GCLID", value: lead.gclid, hideWhenEmpty: true },
            ]}
          />
        </Card>

        <Card>
          <CardHeader icon={MapPin} title="Address" />
          <DetailList
            columns={1}
            items={[
              { label: "Line 1", value: info.addressLine1 ?? lead.address1 },
              { label: "Line 2", value: info.addressLine2 ?? lead.address2, hideWhenEmpty: true },
              { label: "Town or city", value: info.townOrCity },
              { label: "County", value: info.county, hideWhenEmpty: true },
              { label: "Postcode", value: info.postcode },
            ]}
          />
        </Card>

        <Card>
          <CardHeader icon={Car} title="Vehicle" />
          <DetailList
            columns={1}
            items={[
              { label: "Registration", value: info.vehicleReg },
              {
                label: "Make and model",
                value: [info.vehicleMake, info.vehicleModel].filter(Boolean).join(" "),
              },
              { label: "Year", value: info.year },
              { label: "Engine size", value: info.engineSize ? `${info.engineSize}cc` : "" },
              { label: "Value", value: info.currentValue ? `£${info.currentValue}` : "" },
              { label: "Seats", value: info.numberOfSeats, hideWhenEmpty: true },
              { label: "Use", value: info.useOfVehicle, hideWhenEmpty: true },
              { label: "Annual mileage", value: info.annualMileage, hideWhenEmpty: true },
            ]}
          />
        </Card>

        <Card>
          <CardHeader icon={FileJson} title="Licence" />
          <DetailList
            columns={1}
            items={[
              { label: "Licence type", value: info.typeOfDrivingLicence },
              { label: "Driving licence number", value: info.drivingLicenceNumber },
              { label: "Test pass date", value: formatDate(info.testPassDate) },
            ]}
          />
        </Card>
      </div>

      {extraItems.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            icon={FileJson}
            title="Everything else submitted"
            description="All remaining fields captured by the step form."
          />
          <DetailList columns={3} items={extraItems} />
        </Card>
      )}

      {arraySections.map((section) => (
        <Card key={section.label} className="mt-4">
          <CardHeader
            title={section.label}
            description={`${section.rows.length} entr${section.rows.length === 1 ? "y" : "ies"}`}
          />
          <div className="divide-y divide-line">
            {section.rows.map((row, index) => (
              <div key={index} className="py-3 first:pt-0 last:pb-0">
                <DetailList
                  columns={2}
                  items={Object.entries(row)
                    .filter(
                      ([, value]) =>
                        typeof value !== "object" &&
                        value !== null &&
                        value !== undefined &&
                        value !== "",
                    )
                    .map(([key, value]) => ({
                      label: formatLabel(key),
                      value: renderValue(value),
                    }))}
                />
              </div>
            ))}
          </div>
        </Card>
      ))}
    </>
  );
}
